"use client";

import { useRef, useState } from "react";
import { Label } from "@/shadcn-components/ui/label";
import { Input } from "@/shadcn-components/ui/input";
import { Textarea } from "@/shadcn-components/ui/textarea";
import { Switch } from "@/shadcn-components/ui/switch";
import { Badge } from "@/shadcn-components/ui/badge";
import { Button } from "@/shadcn-components/ui/button";
import { X, Upload } from "lucide-react";
import { MAX_ICON_BYTES } from "@/lib/custom-indicators/limits";
import type {
  CustomIndicatorProject,
  CustomIndicatorProjectKind,
} from "@/lib/custom-indicators/types";
import CustomIndicatorIcon from "../CustomIndicatorIcon";

interface NavigatorSettingsSectionProps {
  project: CustomIndicatorProject;
  onUpdateMeta: (
    patch: Partial<
      Pick<
        CustomIndicatorProject,
        | "name"
        | "version"
        | "description"
        | "overlay"
        | "tags"
        | "kind"
        | "iconFilePath"
        | "iconDataUrl"
      >
    >
  ) => void;
  onImportIcon: (file: File) => Promise<void>;
}

export default function NavigatorSettingsSection({
  project,
  onUpdateMeta,
  onImportIcon,
}: NavigatorSettingsSectionProps) {
  const [tagInput, setTagInput] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [iconError, setIconError] = useState<string | null>(null);

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (!tag || project.tags.includes(tag)) {
      setTagInput("");
      return;
    }
    onUpdateMeta({ tags: [...project.tags, tag] });
    setTagInput("");
  };

  const removeTag = (tag: string) => {
    onUpdateMeta({ tags: project.tags.filter((t) => t !== tag) });
  };

  return (
    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3">
      <p className="mb-3 text-[10px] text-muted-foreground">
        Metadata is stored in the .csproj (Visual Studio style) and synced on Save
      </p>
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Project type</Label>
          <select
            value={project.kind}
            onChange={(e) =>
              onUpdateMeta({
                kind: e.target.value as CustomIndicatorProjectKind,
              })
            }
            className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
          >
            <option value="indicator">Custom Indicator (chart)</option>
            <option value="trading-algo">Trading Algo (Expert Advisor)</option>
          </select>
          {project.kind === "trading-algo" && (
            <p className="text-[10px] text-amber-600 dark:text-amber-500">
              Trading algo execution is not available yet — project scaffold only.
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Indicator icon</Label>
          <div className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5">
            <CustomIndicatorIcon
              name={project.name}
              iconDataUrl={project.iconDataUrl}
              size={48}
            />
            <div className="min-w-0 flex-1 space-y-1">
              <p className="truncate font-mono text-[10px] text-muted-foreground">
                {project.iconFilePath ?? "No icon configured"}
              </p>
              <p className="text-[10px] text-muted-foreground">
                SVG, PNG, or WebP · max {Math.round(MAX_ICON_BYTES / 1024)} KB · 256×256
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 gap-1.5 text-xs"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-3 w-3" />
                Upload icon
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".svg,.png,.webp,image/svg+xml,image/png,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  setIconError(null);
                  void onImportIcon(file).catch((err) => {
                    setIconError(
                      err instanceof Error ? err.message : "Failed to import icon"
                    );
                  });
                }}
              />
              {iconError ? (
                <p className="text-[10px] text-destructive">{iconError}</p>
              ) : null}
            </div>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Name</Label>
          <Input
            value={project.name}
            onChange={(e) => onUpdateMeta({ name: e.target.value })}
            className="h-8 text-xs"
            placeholder="Indicator name"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Version</Label>
          <Input
            value={project.version}
            onChange={(e) => onUpdateMeta({ version: e.target.value })}
            className="h-8 text-xs"
            placeholder="1.0"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Description</Label>
          <Textarea
            value={project.description}
            onChange={(e) => onUpdateMeta({ description: e.target.value })}
            className="min-h-[72px] resize-none text-xs"
            placeholder="Describe this indicator…"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">
            Entry file (relative path)
          </Label>
          <Input
            value={project.entryFilePath}
            readOnly
            className="h-8 font-mono text-xs text-muted-foreground"
            aria-label="Entry file relative path"
          />
          <p className="text-[10px] text-muted-foreground">
            Main C# entry file (VybeEntryFile in .csproj). Right-click a .cs file in the tree to set as entry.
          </p>
        </div>
        <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5">
          <Label className="text-xs font-normal">Chart placement</Label>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {project.overlay
              ? "Overlay on main price chart"
              : "Separate indicator pane"}
            {" "}
            — set via{" "}
            <code className="font-mono">[Indicator(Overlay=…)]</code> in the
            entry .cs file, then recompile.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Tags</Label>
          <div className="flex gap-1.5">
            <Input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addTag();
                }
              }}
              className="h-7 flex-1 text-xs"
              placeholder="Add tag…"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={addTag}
            >
              Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5 pt-1">
            {project.tags.map((tag) => (
              <Badge
                key={tag}
                variant="secondary"
                className="gap-1 text-[10px]"
              >
                {tag}
                <button
                  type="button"
                  className="rounded-sm hover:text-destructive"
                  onClick={() => removeTag(tag)}
                  aria-label={`Remove tag ${tag}`}
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </Badge>
            ))}
          </div>
        </div>
        <div className="rounded-lg border border-border px-3 py-2 text-[10px] text-muted-foreground">
          <p>
            Project size: {(project.sizeBytes / 1024).toFixed(1)} KB
          </p>
        </div>
      </div>
    </div>
  );
}
